import React, { useState } from 'react';
import styled from 'styled-components';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  width: 400px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const Input = styled.input`
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const TextArea = styled.textarea`
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  min-height: 100px;
`;

const Button = styled.button`
  padding: 10px;
  background-color: #316C6E;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #254F51;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TypeSelect = styled.select`
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 5px;

  &:invalid {
    color: #666;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: -10px;
  margin-bottom: 10px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const PriceInput = styled(Input)`
  width: 150px;
`;

const CustomActivityModal = ({ onClose, onSave, selectedTimeSlot }) => {
  const { language } = useLanguage();
  const t = translations[language].customActivity;
  
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [description, setDescription] = useState('');
  const [addLocation, setAddLocation] = useState(false);
  const [location, setLocation] = useState({ lat: null, lng: null });
  const [errors, setErrors] = useState({});
  const [price, setPrice] = useState('');

  const activityTypes = [
    { value: '', label: t.types.select },
    { value: 'task', label: t.types.task },
    { value: 'hotel', label: t.types.hotel },
    { value: 'exploration', label: t.types.exploration },
    { value: 'rent_a_car', label: t.types.rent_a_car },
    { value: 'restaurant', label: t.types.restaurant },
    { value: 'poi', label: t.types.poi }
  ];

  const handleMapClick = (e) => {
    setLocation({ lat: e.latlng.lat, lng: e.latlng.lng });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = t.validation.nameRequired;
    if (!type) newErrors.type = t.validation.typeRequired;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const activityData = {
      type,
      name,
      description,
      day: selectedTimeSlot.day,
      hour: selectedTimeSlot.hour,
      duration: 1,
      price: price ? parseInt(price) : null,
      ...(location.lat && location.lng ? { lat: location.lat, lng: location.lng } : {})
    };

    onSave(activityData);
    onClose();
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <h2>{t.title}</h2>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <TypeSelect
              value={type}
              onChange={(e) => setType(e.target.value)}
              required
            >
              {activityTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </TypeSelect>
            {errors.type && <ErrorMessage>{errors.type}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Input
              type="text"
              placeholder={`${t.name} *`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <TextArea
              placeholder={t.description}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </FormGroup>

          <ToggleContainer>
            <input
              type="checkbox"
              checked={addLocation}
              onChange={() => setAddLocation(!addLocation)}
            />
            <span>{t.addLocation}</span>
          </ToggleContainer>

          {addLocation && (
            <MapContainer
              center={[18.7357, -70.1627]}
              zoom={8}
              minZoom={7}
              maxBounds={[
                [17.5, -72],
                [19.9, -68]
              ]}
              style={{ height: '200px', width: '100%' }}
              whenCreated={(map) => {
                map.on('click', handleMapClick);
              }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {location.lat && location.lng && (
                <Marker
                  position={[location.lat, location.lng]}
                  icon={L.icon({
                    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
                    iconSize: [25, 41],
                    iconAnchor: [12, 41],
                    popupAnchor: [1, -34],
                    shadowSize: [41, 41]
                  })}
                />
              )}
            </MapContainer>
          )}

          <FormGroup>
            <PriceInput
              type="number"
              placeholder={`${t.price} (${t.optional})`}
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              min="0"
            />
          </FormGroup>

          <Button type="submit">{t.saveActivity}</Button>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default CustomActivityModal;
